import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { FileSignature } from 'lucide-react';
import { GenericMessage } from 'components/feedback/Message';
import { toast } from 'react-toastify';
import { GetGroupContractMandatsResponse, GroupContractMandatItemDto } from 'api/contract';
import { Button } from 'components/ui/Button';
import { GenericCard } from 'components/display/Card/Card';
import GenericBadge from 'components/ui/Badge';
import { GenericColors } from 'enums/genericColors';
import { ContractStatus } from 'features/subscription-process/contractualization-process/types/enums';
import { getContractStatusLabel } from 'features/subscription-process/contractualization-process/utils/getContractCardInfos';
import { contractsAPI } from 'services/swagger-api';
import { Sizes } from 'enums/sizes';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { usePanel } from 'hooks/usePanel';
import { NsKey } from 'enums/NsKey';
import { useGroupContractProcess } from '../../contexts/GroupContractProcessContext';
import { MandatLockedSummary } from './MandatLockedSummary';

interface MandatsCardProps {
  /** Notifie le parent quand tous les mandats sont signés (utilisé pour débloquer Signer le contrat). */
  onAllSignedChange?: (allSigned: boolean) => void;
  /** Désactive les boutons "Demander la signature" tant que pré-requis pas remplis (CP + souscripteur). */
  disabled?: boolean;
}

/**
 * Liste des mandats SEPA uniques du contrat (1 par paire IBAN débiteur/créditeur).
 * - Masquée si paiementParAliments=true (cadre, chaque aliment signe son propre mandat).
 * - Sinon : 1 GenericCard par mandat avec :
 *   - Header cliquable → ouvre le panel résumé en lecture seule
 *   - Body (si non signé) → bouton "Demander la signature" / "Renvoyer la demande"
 */
export const MandatsCard = ({ onAllSignedChange, disabled = false }: MandatsCardProps) => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);
  const { groupContractId, dataVersion } = useGroupContractProcess();
  const { openPanel, setPanelActions } = usePanel();

  const [data, setData] = useState<GetGroupContractMandatsResponse | null>(null);
  // Loader visible uniquement au premier chargement : les rechargements suivants
  // (dataVersion, après signature) mettent la liste à jour en place, sans flash.
  const [loading, setLoading] = useState(true);
  const [triggeringMandatId, setTriggeringMandatId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await contractsAPI.getGroupContractMandats({ groupContractId });
      setData(response);
    } finally {
      setLoading(false);
    }
  }, [groupContractId]);

  // Recharge au mount puis à chaque refresh global (action ou notification SignalR),
  // pour refléter les mandats signés sans rechargement manuel de la page.
  useEffect(() => { load(); }, [load, dataVersion]);

  const mandats = data?.mandats ?? [];
  const allSigned = mandats.length > 0 && mandats.every(m => m.signatureStatus === 'signed');

  // Si paiement par aliments : pas de mandats au niveau contrat → considérer la condition
  // remplie pour permettre la signature du contrat (chaque aliment signera son propre mandat).
  const effectivelyComplete = data?.paymentByAliments === true || allSigned;

  useEffect(() => {
    onAllSignedChange?.(effectivelyComplete);
  }, [effectivelyComplete, onAllSignedChange]);

  const handleSignMandat = useCallback(async (mandatId: string) => {
    setTriggeringMandatId(mandatId);
    try {
      const response = await contractsAPI.triggerGroupContractSingleMandatSignature({
        groupContractId,
        mandatId,
      });
      if (response.isSuccess === false) {
        toast.error(response.errorMessage ?? t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.ERROR_SEND'));
      } else {
        toast.success(t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.SUCCESS_SEND'));
        await load();
      }
    } catch (err: any) {
      toast.error(err?.message ?? t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.ERROR_SEND'));
    } finally {
      setTriggeringMandatId(null);
    }
  }, [groupContractId, load, t]);

  const handleOpenMandat = useCallback((mandat: GroupContractMandatItemDto) => {
    openPanel(
      {
        title: mandat.creditorName ?? t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.CREDITOR_UNKNOWN'),
        size: Sizes['3xl'],
      },
      <MandatLockedSummary mandat={mandat} />,
    );
    setPanelActions(undefined);
  }, [openPanel, setPanelActions, t]);

  // Masquée si paiement par aliments (CADRE seul) → géré au niveau du parent.
  if (data?.paymentByAliments) {
    return null;
  }

  if (loading) {
    return (
      <p className="text-sm text-gray-500">
        {t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.LOADING')}
      </p>
    );
  }

  if (mandats.length === 0) {
    return (
      <GenericMessage
        type="info"
        dismissible={false}
        message={t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.EMPTY')}
      />
    );
  }

  return (
    <div className={mandats.length === 2 ? 'grid grid-cols-2 gap-3' : 'flex flex-col gap-3'}>
      {mandats.map(m => (
        <MandatCardItem
          key={m.id}
          mandat={m}
          onOpenSummary={() => handleOpenMandat(m)}
          onSign={() => handleSignMandat(m.id)}
          isSigning={triggeringMandatId === m.id}
          disabled={disabled}
          t={t}
        />
      ))}
    </div>
  );
};

interface MandatCardItemProps {
  mandat: GroupContractMandatItemDto;
  onOpenSummary: () => void;
  onSign: () => void;
  isSigning: boolean;
  disabled: boolean;
  t: (k: string) => string;
}

/** Card individuelle d'un mandat — Header + Body, pattern aligné sur SignMandateCard simu. */
const MandatCardItem = ({
  mandat,
  onOpenSummary,
  onSign,
  isSigning,
  disabled,
  t,
}: MandatCardItemProps) => {
  const isSigned = mandat.signatureStatus === 'signed';
  const isPending = mandat.signatureStatus === 'pending';

  // Alignement strict sur le pattern simu (SignMandateCard) :
  // - signé → Green + ContractStatus.SIGNED ("Signé")
  // - en attente → Yellow + ContractStatus.WAITING_SIGNATURE ("En attente de signature")
  // - à envoyer (draft) → Yellow + ContractStatus.TO_SIGN ("À signer")
  const badgeColor = isSigned ? GenericColors.Green : GenericColors.Yellow;
  const statusLabel = getContractStatusLabel(
    isSigned
      ? ContractStatus.SIGNED
      : isPending
      ? ContractStatus.WAITING_SIGNATURE
      : ContractStatus.TO_SIGN,
  );

  // Le bouton du body est cliqué avec stopPropagation pour ne pas bubbler au header
  // (qui ouvrirait à tort le panel résumé).
  const handleSignClick = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    onSign();
  }, [onSign]);

  return (
    <GenericCard>
      <GenericCard.Header
        icon={<FileSignature className="w-7 h-7 text-primary" />}
        subtitle={mandat.creditorIbanMasked ?? undefined}
        onClick={onOpenSummary}
        titleAside={
          <GenericBadge
            color={badgeColor}
            label={statusLabel}
            badgeType="status"
          />
        }
      >
        {mandat.creditorName ?? t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.CREDITOR_UNKNOWN')}
      </GenericCard.Header>
      {!isSigned && (
        <GenericCard.Body>
          <div className="flex items-center justify-center">
            <Button
              color="secondary"
              round
              onClick={handleSignClick}
              disabled={isSigning || disabled}
            >
              {isSigning
                ? t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.SENDING')
                : isPending
                ? t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.RESEND_ONE')
                : t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.SEND_ONE')}
            </Button>
          </div>
        </GenericCard.Body>
      )}
    </GenericCard>
  );
};
